import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "../../../lib/api";
import { ImageType } from "../../../interface/transportation";
import {
  CHECK_IMAGE,
  CHECK_IMAGE_INFO,
  CHECK_TRANSPORTATION_IMAGE_DELETE,
  CHECK_TRANSPORTATION_IMAGE_SAVE,
} from "../../../common/query-keys";
import * as S from "../../../components/organisms/TaskImgPageContent/taskImgPageContent.styles";
import ImgTemplates from "../../../components/template/ImgTemplates";
import TaskImgPageContent from "../../../components/organisms/TaskImgPageContent";
import { ReactComponent as IconCancel } from "../../../styles/assets/svg/icon-cancel.svg";
import CheckTransportationModal from "../../../components/molcules/Modal/CheckTransportationModal";
import CheckTransportationDeleteModal from "../../../components/molcules/Modal/CheckTransportationDeleteModal";
import dayjs from "dayjs";
import TaskImgBackModal from "../../../components/molcules/Modal/TaskImgBackModal";
import imageCompression from "browser-image-compression";
import { useLocation, useNavigate } from "react-router";
import { Loading } from "../../../components/atoms/Loading"; // 기존 Loading 컴포넌트 임포트
import styled from "styled-components";

// 오버레이 스타일 컴포넌트 (위에서 작성한 코드)
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const TaskImgUploadPage = (props: any) => {
  /** ============================= state 영역 ============================= */
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // 저장하지 않은 이미지 담는 STATE
  const [imgUrl, setImgUrl] = useState<any>([]);
  // 저장한 이미지 담는 STATE
  const [imgArr, setImgArr] = useState<ImageType[]>([]);
  const [isModal, setIsModal] = useState<boolean>(false);
  const [isImgBig, setIsImgBig] = useState<boolean>(false);
  const [isSelectedImg, setIsSelectedImg] = useState<any | ImageType>("");
  const [isUploading, setIsUploading] = useState(false);
  // false -> 수정, true -> 삭제
  const [isEditDelete, setIsEditDelete] = useState<boolean>(false);
  const [isDeleteImg, setIsDeleteImg] = useState<any[]>([]);
  const [isDeleteModal, setIsDeleteModal] = useState<boolean>(false);
  const [isBackModal, setIsBackModal] = useState<boolean>(false);
  const [groupedImages, setGroupedImages] = useState({});

  const inputRef = useRef<HTMLInputElement>(null);

  /** ============================= API 영역 ============================= */
  const { mutateAsync: putCheckListImageData } = useMutation({
    mutationFn: (data: any) => {
      return request({
        method: "PUT",
        url: CHECK_TRANSPORTATION_IMAGE_DELETE,
        data: data,
      });
    },
    onSuccess: (res: any) => {
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: [CHECK_IMAGE_INFO] });
        setIsDeleteModal(false);
        setIsDeleteImg([]);
        setIsEditDelete(false);
      }
    },
    onError: (e: any) => {
      console.error("@@ E: ", e);
      alert(e.message);
    },
  });

  // 사진추가 버튼 클릭시 실행 함수
  const { mutateAsync: ImageUpload } = useMutation({
    mutationFn: (data: FormData) => {
      return request({
        method: "POST",
        url: CHECK_IMAGE,
        data: data,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onError: () => {
      alert("이미지 업로드에 실패했습니다.");
    },
  });

  /** 이미지 DB에 저장하는 함수 */
  const { mutateAsync: postCheckListImageData } = useMutation({
    mutationFn: (data: any) => {
      return request({
        method: "POST",
        url: CHECK_TRANSPORTATION_IMAGE_SAVE,
        data: data,
      });
    },
    onSuccess: (res: any) => {
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: [CHECK_IMAGE_INFO] });
        setImgUrl([]);
        setIsModal(true);
      }
    },
    onError: (e: any) => {
      console.error("@@ E: ", e);
      alert(e.message);
    },
  });

  /** 이미지 조회 */
  useQuery({
    queryKey: [CHECK_IMAGE_INFO],
    queryFn: async () => {
      const result = await request<ImageType[]>({
        method: "GET",
        url: `${CHECK_IMAGE_INFO}/${location.state.transportationId}`,
      });

      setGroupedImages(groupImagesByDate(result));
      setImgArr(result);
      return result;
    },
  });

  /** ============================= 비즈니스 로직 영역 ============================= */
  const onImgDeleteListHandler = () => {
    setIsDeleteModal(true);
  };

  const onDeleteModalCancelHandler = () => {
    setIsDeleteModal(false);
  };

  const onSaveHanlder = async () => {
    await postCheckListImageData({
      imgUrl,
      isNextLevel: location.state.isNextLevel,
      isReceipt: false, // 인수증 사진 등록 여부
    });
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const onModalCloseHanlder = () => {
    setIsModal(false);
  };

  const onBackPageMoveHandler = () => {
    if (imgUrl.length !== 0) {
      setIsBackModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleUploadImage = () => {
    inputRef.current?.click();
  };

  const onImgBigHandler = (imgFile: string) => {
    setIsImgBig(true);
    setIsSelectedImg(imgFile);
  };

  const onImgEditDeleteHandler = () => {
    setIsEditDelete(!isEditDelete);
  };

  const onDeleteImgListSelectedHandler = (data: any) => {
    if (isDeleteImg.includes(data)) {
      setIsDeleteImg((prev) => prev.filter((item: any) => item !== data));
    } else {
      setIsDeleteImg((prev) => [...prev, data]);
    }
  };

  const onModalDeleteCheckHanlder = async () => {
    setIsDeleteImg((prev) =>
      prev?.filter((item) => {
        if (!item?.id) {
          setImgUrl((prevImgUrl: any) =>
            prevImgUrl?.filter((data: any) => item.imagename !== data.imagename)
          );
          setGroupedImages((prevGroupedImages: any) => {
            if (!prevGroupedImages) return {};
            const updatedGroupedImages = { ...prevGroupedImages };
            Object.keys(updatedGroupedImages).forEach((date) => {
              updatedGroupedImages[date] = updatedGroupedImages[date].filter(
                (data: any) => data.imagename !== item.imagename
              );
            });
            return updatedGroupedImages;
          });
        }
        return !!item?.id;
      })
    );
    await putCheckListImageData(isDeleteImg);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  /** 이미지 날짜별/단계별로 그룹화 */
  const groupImagesByDate = (images: any) => {
    return images?.reduce((acc: any, image: any) => {
      const level = image.currentLevel;
      if (!acc[level]) {
        acc[level] = [];
      }
      acc[level].push(image);
      return acc;
    }, {});
  };

  /** 이미지 업로드시 이미지 변경 함수 */
  const onChangeImg = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    try {
      setIsUploading(true);
      const files = Array.from(e.target.files);
      const formData = new FormData();

      // 이미지 압축 옵션 설정
      const options = {
        maxSizeMB: 0.3, // 최대 파일 크기 (MB)
        maxWidthOrHeight: 1920, // 최대 너비/높이
        useWebWorker: true,
        initialQuality: 0.8, // 초기 품질 설정
        onProgress: (progress: number) => {
          console.log("압축 진행률:", progress);
        },
      };

      // 모든 파일을 순차적으로 압축 및 처리
      const compressedFiles = await Promise.all(
        files.map(async (file) => {
          try {
            // 파일 타입 검증
            if (!file.type.startsWith("image/")) {
              throw new Error("이미지 파일만 업로드 가능합니다.");
            }

            const compressedFile = await imageCompression(file, options);

            // 압축 결과 검증
            if (compressedFile.size > file.size) {
              console.log("압축 효과가 없어 원본 파일 사용");
              return file;
            }

            return new File([compressedFile], file.name, { type: file.type });
          } catch (err) {
            console.error(`파일 압축 중 오류 발생: ${file.name}`, err);
            throw err;
          }
        })
      );

      // FormData에 압축된 파일 추가
      compressedFiles.forEach((file) => {
        formData.append("image", file);
      });

      // 이미지 업로드 API 호출
      const result = await ImageUpload(formData);

      // 서버 응답 검증
      if (!Array.isArray(result)) {
        throw new Error("서버 응답이 올바르지 않습니다.");
      }

      const newImages = result.map((resItem) => ({
        transportationId: location.state.transportationId,
        imagename: resItem.originalname,
        size: resItem.size,
        imageUrl: `transportation/check-list/image/${resItem.filename}`,
        updatedAt: new Date(),
      }));

      setImgUrl((prev: any) => [...newImages, ...prev]);
    } catch (error: any) {
      console.error("이미지 업로드 중 오류:", error);
      alert(error.message || "이미지 업로드 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  /** ============================= useEffect 영역 ============================= */
  useEffect(() => {
    setIsDeleteImg([]);
  }, [isEditDelete]);

  /** ============================= 컴포넌트 영역 ============================= */
  return (
    <>
      <ImgTemplates pageTitle="사진등록" padding="0px">
        <TaskImgPageContent
          inputRef={inputRef}
          imgArr={imgArr}
          imgUrl={imgUrl}
          isUploading={isUploading}
          isEditDelete={isEditDelete}
          isDeleteImg={isDeleteImg}
          onChangeImg={onChangeImg}
          groupedImages={groupedImages}
          handleUploadImage={handleUploadImage}
          onSaveHanlder={onSaveHanlder}
          onImgBigHandler={onImgBigHandler}
          onImgEditDeleteHandler={onImgEditDeleteHandler}
          onBackPageMoveHanlder={onBackPageMoveHandler}
          onDeleteImgListSelectedHandler={onDeleteImgListSelectedHandler}
          onImgDeleteListHandler={onImgDeleteListHandler}
        />
      </ImgTemplates>

      {/* isUploading 값이 true이면 Loading 컴포넌트를 오버레이로 표시 */}
      {isUploading && (
        <LoadingOverlay>
          <Loading width={60} height={60} />
        </LoadingOverlay>
      )}

      {isModal && (
        <CheckTransportationModal onModalCloseHanlder={onModalCloseHanlder} />
      )}
      {isImgBig && (
        <S.TaskImgBigModal>
          <S.TaskImgBigModalLayout>
            <S.TaskImgBigModalBox>
              {isSelectedImg.originalname !== undefined && (
                <img
                  src={`${process.env.REACT_APP_API_HOST}/transportation/check-list/image/${isSelectedImg?.filename}`}
                  alt=""
                />
              )}
              {isSelectedImg.imagename !== undefined && (
                <img
                  src={`${process.env.REACT_APP_API_HOST}/${isSelectedImg.imageUrl}`}
                  alt=""
                />
              )}
              <S.CloseButton onClick={() => setIsImgBig(false)}>
                <IconCancel fill="#888888" />
              </S.CloseButton>
            </S.TaskImgBigModalBox>
          </S.TaskImgBigModalLayout>
        </S.TaskImgBigModal>
      )}
      {isDeleteModal && (
        <CheckTransportationDeleteModal
          onDeleteModalCancelHandler={onDeleteModalCancelHandler}
          onModalDeleteCheckHanlder={onModalDeleteCheckHanlder}
          isDeleteImg={isDeleteImg}
        />
      )}
      {isBackModal && (
        <TaskImgBackModal onModalCloseHanlder={() => setIsBackModal(false)} />
      )}
    </>
  );
};

export default TaskImgUploadPage;
